import { Modal, ModalVariant } from '@ben/orion/modals';
import { ButtonVariant } from '@ben/orion/deprecated-button';
import Icon, { IconName } from '@ben/orion/icon';
import InlineTextButton from '@ben/orion/inline-text-button';
import MultiSelect from '@ben/orion/multi-select';
import RadioButton from '@ben/orion/radio-button';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { IModalRef } from '@ben/shared/components/modal/modal';
import { type IModularBenefitDetail } from '@ben/shared/types/columbia';
import { LifeEventType } from '@ben/shared/types/life-event';
import { useMemo, useRef } from 'react';
import {
  Controller,
  useFormContext,
  useWatch,
  type Control,
} from 'react-hook-form';
import styled, { css } from 'styled-components';
import { fieldCannotBeBlankErrorMessage } from '../../shared/constants';
import type { StepsFields } from '../elements/types';
import LifeEventRestrictions from './life-event-restrictions';
import LifeEventWindows from './life-event-windows';

interface LifeEventRootCardProps {
  index: number;
  isExpanded: boolean;
  onRemove: (index: number) => void;
  onToggle: (index: number) => void;
  mainControl: Control<StepsFields>;
}

const EMPTY_LIFE_EVENT_TYPES: string[] = [];

const formatLifeEventTypeLabel = (lifeEventType: string): string =>
  lifeEventType
    .replaceAll('_OR_', '/')
    .replaceAll('_', ' ')
    .toLowerCase()
    .replaceAll(/\b\w/g, (letter) => letter.toUpperCase());

const LifeEventRootCard = ({
  index,
  isExpanded,
  onRemove,
  onToggle,
  mainControl,
}: LifeEventRootCardProps) => {
  const { control } = useFormContext<IModularBenefitDetail>();
  const selectedLifeEventTypes =
    useWatch({
      control,
      name: `lifeEventPolicies.${index}.type`,
      defaultValue: EMPTY_LIFE_EVENT_TYPES,
    }) ?? EMPTY_LIFE_EVENT_TYPES;

  const removeStepModalRef = useRef<IModalRef>(null);
  const collapsedLifeEventTitle = useMemo(() => {
    const defaultTitle = `Life event #${index + 1}`;

    if (isExpanded || selectedLifeEventTypes.length === 0) {
      return defaultTitle;
    }

    const selectedTypeLabels = selectedLifeEventTypes.map((lifeEventType) =>
      formatLifeEventTypeLabel(lifeEventType)
    );
    return `${defaultTitle} (${selectedTypeLabels.join(', ')})`;
  }, [index, isExpanded, selectedLifeEventTypes]);

  const handleRemoveStep = (event: React.MouseEvent) => {
    event.stopPropagation();
    removeStepModalRef.current?.showModal();
  };

  const handleRemoveStepConfirmation = () => {
    onRemove(index);
  };

  return (
    <Wrapper id={`life-event-root-card-${index}`}>
      <LifeEventRootCardHeader onClick={() => onToggle(index)}>
        <LifeEventRootCardHeaderIconTextWrapper>
          <Chevron $isOpen={isExpanded} name={IconName.ArrowsDown} />
          <Typography variant={TypographyVariant.h3}>
            {collapsedLifeEventTitle}
          </Typography>
        </LifeEventRootCardHeaderIconTextWrapper>
        <InlineTextButton onClick={handleRemoveStep} label="Remove" />
      </LifeEventRootCardHeader>

      {isExpanded && (
        <>
          <Typography variant={TypographyVariant.bodyMd1}>
            All fields below are required, unless specified optional.
          </Typography>
          <Controller
            name={`lifeEventPolicies.${index}.type`}
            control={control}
            rules={{ required: fieldCannotBeBlankErrorMessage }}
            render={({ field, fieldState }) => (
              <MultiSelect
                {...field}
                label="Life Event Type"
                width="100%"
                data={Object.values(LifeEventType).map((type) => ({
                  value: type,
                  label: formatLifeEventTypeLabel(type),
                }))}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
          <ApprovalSettingsContainer>
            <ApprovalSettingsHeader>
              <Typography variant={TypographyVariant.h3}>
                Approval Setting
              </Typography>
              <Typography variant={TypographyVariant.bodyMd1}>
                When employee submits this life event
              </Typography>
            </ApprovalSettingsHeader>
            <Controller
              name={`lifeEventPolicies.${index}.requiresApproval`}
              control={control}
              render={({ field }) => (
                <RadioContainer>
                  <RadioButton
                    label="Automatically approve"
                    checked={!field.value}
                    onChange={() => field.onChange(false)}
                  />
                  <RadioButton
                    label="Require admin approval"
                    checked={field.value}
                    onChange={() => field.onChange(true)}
                  />
                </RadioContainer>
              )}
            />
          </ApprovalSettingsContainer>

          <ApprovalSettingsContainer>
            <ApprovalSettingsHeader>
              <Typography variant={TypographyVariant.h3}>
                Proof Setting
              </Typography>
              <Typography variant={TypographyVariant.bodyMd1}>
                Whether employees must upload proof for this life event
              </Typography>
            </ApprovalSettingsHeader>
            <Controller
              name={`lifeEventPolicies.${index}.requiresProof`}
              control={control}
              render={({ field }) => (
                <RadioContainer>
                  <RadioButton
                    label="Optional"
                    checked={!field.value}
                    onChange={() => field.onChange(false)}
                  />
                  <RadioButton
                    label="Mandatory"
                    checked={!!field.value}
                    onChange={() => field.onChange(true)}
                  />
                </RadioContainer>
              )}
            />
          </ApprovalSettingsContainer>

          <LifeEventWindows lifeEventIndex={index} />
          <LifeEventRestrictions
            stepsControl={mainControl}
            lifeEventIndex={index}
          />
        </>
      )}
      <Modal
        ref={removeStepModalRef}
        title={`Remove Life event #${index + 1}`}
        description={`Are you sure you want to remove the life event? You will also delete all the internal components.`}
        primaryText="Remove"
        secondaryText="Cancel"
        primaryVariant={ButtonVariant.error}
        onConfirm={handleRemoveStepConfirmation}
        icon={IconName.Trash}
        variant={ModalVariant.Icon}
      />
    </Wrapper>
  );
};

export default LifeEventRootCard;

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    width: 100%;
    padding: ${theme.spacing(3)};
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing(3)};
    align-self: stretch;
    border-radius: 10px;
    background: ${theme.palette.primary.bg};
  `};
`;

const LifeEventRootCardHeader = styled.div`
  width: 100%;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const LifeEventRootCardHeaderIconTextWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(1)};
  `};
`;

const Chevron = styled(Icon)<{ $isOpen: boolean }>`
  transform: rotate(${({ $isOpen }) => ($isOpen ? '0deg' : '-90deg')});
  cursor: pointer;
`;

const ApprovalSettingsContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `};
`;

const ApprovalSettingsHeader = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `}
`;

const RadioContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(3)};
  `}
`;
